// src/components/resources/quizzes/utils/answer-correctness.ts

import type { QuizQuestion } from '~/lib/resources/types/tools/quiz'

/**
 * Single source of truth for grading a quiz answer.
 *
 * This logic was previously implemented THREE times and they disagreed, so the
 * score a user saw on the section-review screen could differ from the final
 * score (Phase-0 P1, quizzes ledger):
 *   - `useQuizState.isAnswerCorrect` (feeds `calculateScore`) graded `number` by
 *     exact string-equality — IGNORING the `tolerance` the NumberQuestion UI
 *     advertises — and graded arrays ORDER-sensitively via an `Array.isArray`
 *     branch that made its dedicated order-insensitive `multiselect` branch
 *     unreachable dead code;
 *   - `useQuizState.handleAnswer`'s inline `isCorrect` (feeds `history`) graded
 *     arrays order-INsensitively;
 *   - `SectionReview.isAnswerCorrect` (the review screen) was the correct one.
 *
 * This is `SectionReview`'s reference behaviour, now shared by all three call
 * sites: case-insensitive `text`, tolerance-aware `number`, order-insensitive
 * `multiselect`, order-sensitive `ordered`.
 */
export function isAnswerCorrect(
  question: QuizQuestion,
  answer: string | string[] | undefined,
): boolean {
  if (!answer) return false

  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (question.type) {
    case 'text':
      return (
        typeof answer === 'string' &&
        typeof question.correctAnswer === 'string' &&
        answer.trim().toLowerCase() === question.correctAnswer.trim().toLowerCase()
      )

    case 'number': {
      const normalizedAnswer = Number(answer)
      const normalizedCorrect = Number(question.correctAnswer)
      if (Number.isNaN(normalizedAnswer) || Number.isNaN(normalizedCorrect)) return false
      return Math.abs(normalizedAnswer - normalizedCorrect) <= (question.tolerance ?? 0)
    }

    case 'multiselect': {
      if (!Array.isArray(answer) || !Array.isArray(question.correctAnswer)) return false
      // Set-equality: a length+includes check would pass duplicate selections
      // (e.g. ['a','a','a'] against ['a','b','c']).
      const selected = new Set(answer)
      const correct = new Set(question.correctAnswer)
      return selected.size === correct.size && [...selected].every((a) => correct.has(a))
    }

    case 'ordered':
      return (
        Array.isArray(answer) &&
        Array.isArray(question.correctAnswer) &&
        answer.length === question.correctAnswer.length &&
        answer.every((a, i) => a === question.correctAnswer[i])
      )

    default:
      return answer === question.correctAnswer
  }
}
